import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { PDFViewer } from "@react-pdf/renderer";
import { tokens } from "../../../theme";
import {
  listExtraInvoicesDetails,
  resetExtraInvoicesDetails,
  updateExtraInvoicesDetails,
} from "../../../store/actions";
import InvoicePDF from "./InvoicePDF";
import { getTaxRateForAmount } from "../../../utils";

const ExtrasBillDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [showPreview, setShowPreview] = useState(false);
  const [editedExtras, setEditedExtras] = useState([]);
  const [totals, setTotals] = useState({
    amount: 0,
    sgst: 0,
    cgst: 0,
    total: 0,
  });
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [customer, setCustomer] = useState({});
  const [bookingDetails, setBookingDetails] = useState({
    booking_id: "",
    inv_no: "",
    remarks: "",
  });

  const { extraInvoicesDetails, hotelProfile, isLoading } = useSelector(
    (state) => {
      return {
        extraInvoicesDetails: state.admin.extraInvoicesDetails,
        hotelProfile: state.registration.hotelProfile,
        isLoading: state.loading,
      };
    }
  );
  const gstEnabled = Boolean(hotelProfile?.gst_enabled);

  useEffect(() => {
    document.title = "Extras Invoice Details";
    if (id) {
      dispatch(resetExtraInvoicesDetails());
      dispatch(listExtraInvoicesDetails({ id }));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (extraInvoicesDetails) {
      setEditedExtras(extraInvoicesDetails.extras || []);
      setCustomer(extraInvoicesDetails.customer_details || {});
      setPaymentStatus(extraInvoicesDetails.pmt_status || "unpaid");
      setBookingDetails({
        booking_id: extraInvoicesDetails.booking_id || "",
        inv_no: extraInvoicesDetails.inv_no || "",
        remarks: extraInvoicesDetails.remarks || "",
        paid_amount: extraInvoicesDetails.paid_amount || 0,
      });
    }
  }, [extraInvoicesDetails]);

  useEffect(() => {
    let amount = 0;
    let sgst = 0;
    let cgst = 0;

    editedExtras.forEach((item) => {
      const isTaxRow =
        item.particulars.toLowerCase().includes("sgst") ||
        item.particulars.toLowerCase().includes("cgst");
      if (!isTaxRow) {
        amount += item.amount || 0;
      }
      sgst += item.sgst || 0;
      cgst += item.cgst || 0;
    });

    setTotals({
      amount: parseFloat(amount.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      total: parseFloat((amount + sgst + cgst).toFixed(2)),
    });
  }, [editedExtras]);

  const pdfData = useMemo(() => {
    return {
      ...extraInvoicesDetails,
      ...totals,
      extras: editedExtras,
      customer_details: customer,
      pmt_status: paymentStatus,
      ...bookingDetails,
      hotelDetails: {
        name: hotelProfile?.name || "",
        address: hotelProfile?.address || "",
        phone: hotelProfile?.phone || "",
        mobile: hotelProfile?.mobile || "",
        email: hotelProfile?.email || "",
        gstin: hotelProfile?.gstin || "",
        logo: hotelProfile?.logo || "",
        bank_details: hotelProfile?.bank_details || {},
        cin: hotelProfile?.cin || "",
        pan: hotelProfile?.pan || "",
        trade_name: hotelProfile?.trade_name || "",
        gstEnabled,
      },
    };
  }, [
    extraInvoicesDetails,
    totals,
    editedExtras,
    customer,
    paymentStatus,
    hotelProfile,
    bookingDetails,
  ]);

  const handleFieldChange = (index, field, value) => {
    const updated = [...editedExtras];
    if (!updated[index]) return;

    // Per-unit rate from the current row, captured before quantity/amount are overwritten.
    const prevQty = parseInt(updated[index].quantity) || 0;
    const prevAmount = parseFloat(updated[index].amount) || 0;
    const unitRate = prevQty ? prevAmount / prevQty : 0;

    updated[index][field] =
      field === "quantity" || field === "amount"
        ? parseFloat(value) || 0
        : value;

    if (field === "quantity") {
      const activityId = updated[index].activity_id;
      const qty = parseInt(value) || 0;
      updated[index].amount = parseFloat((qty * unitRate).toFixed(2));

      const taxRate = getTaxRateForAmount(updated[index].amount);
      const halfTax = taxRate / 2;
      const sgstValue = parseFloat(
        ((updated[index].amount * halfTax) / 100).toFixed(2)
      );
      const cgstValue = sgstValue;

      updated.forEach((item, i) => {
        if (item.activity_id === activityId) {
          const isSGST = item.particulars.toLowerCase().includes("sgst");
          const isCGST = item.particulars.toLowerCase().includes("cgst");

          if (isSGST) {
            updated[i].amount = sgstValue;
            updated[i].sgst = sgstValue;
            updated[i].cgst = 0;
            updated[
              i
            ].particulars = `SGST @ ${halfTax}% on ${updated[index].particulars} (${qty}x)`;
          }

          if (isCGST) {
            updated[i].amount = cgstValue;
            updated[i].cgst = cgstValue;
            updated[i].sgst = 0;
            updated[
              i
            ].particulars = `CGST @ ${halfTax}% on ${updated[index].particulars} (${qty}x)`;
          }
        }
      });
    }

    setEditedExtras(updated);
  };

  const handleCustomerChange = (field, value) => {
    setCustomer((prev) => ({ ...prev, [field]: value }));
  };

  const handleBookingChange = (field, value) => {
    setBookingDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedInvoice = {
      _id: id,
      customer_details: customer,
      pmt_status: paymentStatus,
      ...bookingDetails,
    };
    dispatch(updateExtraInvoicesDetails(updatedInvoice));
  };

  const formatDate = (timestamp) =>
    timestamp
      ? new Date(timestamp * 1000).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "N/A";

  if (isLoading || !extraInvoicesDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h5" gutterBottom>
            Extras Invoice #{bookingDetails.inv_no}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Date: {formatDate(extraInvoicesDetails.inv_date)}
          </Typography>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            <Button
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => setShowPreview(true)}
            >
              Preview PDF
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        {/* Customer Info */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Customer Info
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={customer.name || ""}
                onChange={(e) => handleCustomerChange("name", e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mobile"
                fullWidth
                size="small"
                value={customer.mobile || ""}
                onChange={(e) => handleCustomerChange("mobile", e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                fullWidth
                size="small"
                value={customer.email || ""}
                onChange={(e) => handleCustomerChange("email", e.target.value)}
                margin="dense"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Booking Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Booking Details
          </Typography>
          <Grid container spacing={2}>
            {bookingDetails.booking_id && (
              <Grid item xs={12}>
                <TextField
                  label="Booking ID"
                  fullWidth
                  size="small"
                  value={bookingDetails.booking_id}
                  onChange={(e) =>
                    handleBookingChange("booking_id", e.target.value)
                  }
                  margin="dense"
                  disabled
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Invoice No"
                fullWidth
                size="small"
                value={bookingDetails.inv_no}
                onChange={(e) => handleBookingChange("inv_no", e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel id="payment-status-label">
                  Payment Status
                </InputLabel>
                <Select
                  labelId="payment-status-label"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                fullWidth
                size="small"
                value={bookingDetails.remarks}
                onChange={(e) => handleBookingChange("remarks", e.target.value)}
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Extras Breakdown
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ bgcolor: colors.primary[400], p: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Particulars</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Amount (₹)</TableCell>
              <TableCell>SAC Code</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editedExtras.map((item, index) => (
              <TableRow
                key={item.activity_id ? `${item.activity_id}-${index}` : index}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <TextField
                    value={item.particulars || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "particulars", e.target.value)
                    }
                    size="small"
                    fullWidth
                    disabled                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={item.quantity || 0}
                    onChange={(e) =>
                      handleFieldChange(index, "quantity", e.target.value)
                    }
                    sx={{ width: 80 }}                    disabled
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    value={item.amount || 0}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        "amount",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    size="small"
                    disabled                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={item.sac_code || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "sac_code", e.target.value)
                    }
                    size="small"
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        mt={2}
      >
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper variant="outlined" sx={{ bgcolor: colors.primary[400], p: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography>Subtotal:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>₹{totals.amount.toFixed(2)}</Typography>
              </Grid>
              {gstEnabled && (
                <>
                  <Grid item xs={6}>
                    <Typography>SGST:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography>₹{totals.sgst.toFixed(2)}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography>CGST:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography>₹{totals.cgst.toFixed(2)}</Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">Grand Total:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="h6">₹{totals.total.toFixed(2)}</Typography>
              </Grid>
              {bookingDetails.paid_amount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography>Advance Paid:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography color="success.main">
                      - ₹{bookingDetails.paid_amount.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6">Balance Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography variant="h6">
                      ₹{(totals.total - bookingDetails.paid_amount).toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        maxWidth="lg"
        fullWidth
        open={showPreview}
        onClose={() => setShowPreview(false)}
      >
        <DialogActions sx={{ justifyContent: "space-between", p: "8px 24px" }}>
          <Typography variant="h6">Invoice Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, height: "100vh" }}>
          <PDFViewer width="100%" height="100%">
            <InvoicePDF data={pdfData} />
          </PDFViewer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExtrasBillDetails;
